import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './CacheManagement.css';

export default function CacheManagement() {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);

    const invalidateCache = async (route) => {
        setLoading(true);
        setMessage('');
        setError('');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/cache/invalidate`,
                { route },
                { withCredentials: true }
            );

            if (response.data.success) {
                const routeLabels = {
                    albums: 'Album',
                    programs: 'Programmer',
                    activities: 'Aktiviteter',
                    weeklyExercises: 'Ukeøvelser',
                    analytics: 'Analyser'
                };
                setMessage(`${routeLabels[route] || route} cache tømt! (${response.data.deletedCount} nøkler slettet)`);
                setTimeout(() => setMessage(''), 5000);
                // Refresh stats after clearing
                fetchStats();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Kunne ikke tømme cache');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    const clearAllCache = async () => {
        if (!window.confirm('Er du sikker på at du vil tømme ALL cache? Dette vil påvirke alle endepunkter.')) {
            return;
        }

        setLoading(true);
        setMessage('');
        setError('');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/cache/clear-all`,
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setMessage(`All cache tømt! (${response.data.deletedCount} nøkler slettet)`);
                setTimeout(() => setMessage(''), 5000);
                // Refresh stats after clearing
                fetchStats();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Kunne ikke tømme cache');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/cache/stats`,
                { withCredentials: true }
            );

            if (response.data.success) {
                setStats(response.data.stats);
            }
        } catch (err) {
            console.error('Kunne ikke hente cache-statistikk:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    const cacheRoutes = [
        { 
            key: 'albums', 
            label: 'Album', 
            description: 'Tøm cache for alle album-endepunkter',
            icon: 'fas fa-images'
        },
        { 
            key: 'programs', 
            label: 'Programmer', 
            description: 'Tøm cache for alle program-endepunkter',
            icon: 'fas fa-list'
        },
        { 
            key: 'activities', 
            label: 'Aktiviteter', 
            description: 'Tøm cache for alle aktivitets-endepunkter',
            icon: 'fas fa-running'
        },
        { 
            key: 'weeklyExercises', 
            label: 'Ukeøvelser', 
            description: 'Tøm cache for ukeøvelse-endepunkter',
            icon: 'fas fa-calendar-week'
        },
        { 
            key: 'analytics', 
            label: 'Analyser', 
            description: 'Tøm cache for analyse-endepunkter',
            icon: 'fas fa-chart-bar'
        }
    ];

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Cache-administrasjon</h1>
                <p className="page-description">
                    Administrer cache for forskjellige endepunkter. Å tømme cache vil tvinge frem ferske data ved neste forespørsel.
                </p>
            </div>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}

            <div className="cache-management-container">
                <div className="cache-stats-section">
                    <div className="section-header">
                        <h2>
                            <i className="fas fa-chart-line"></i> Cache-statistikk
                        </h2>
                        <button 
                            onClick={fetchStats} 
                            className="refresh-button"
                            disabled={loadingStats}
                        >
                            <i className="fas fa-sync-alt"></i> Oppdater
                        </button>
                    </div>
                    
                    {loadingStats ? (
                        <div className="loading">Laster statistikk...</div>
                    ) : stats ? (
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Totalt antall cache-nøkler</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.albums}</div>
                                <div className="stat-label">Album</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.programs}</div>
                                <div className="stat-label">Programmer</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.activities}</div>
                                <div className="stat-label">Aktiviteter</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.weeklyExercises}</div>
                                <div className="stat-label">Ukeøvelser</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.analytics}</div>
                                <div className="stat-label">Analyser</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{stats.byRoute.other}</div>
                                <div className="stat-label">Andre</div>
                            </div>
                        </div>
                    ) : (
                        <button onClick={fetchStats} className="load-stats-button">
                            Last statistikk
                        </button>
                    )}
                </div>

                <div className="cache-actions-section">
                    <div className="section-header">
                        <h2>
                            <i className="fas fa-broom"></i> Tøm cache etter rute
                        </h2>
                    </div>

                    <div className="cache-routes-grid">
                        {cacheRoutes.map(route => (
                            <div key={route.key} className="cache-route-card">
                                <div className="route-icon">
                                    <i className={route.icon}></i>
                                </div>
                                <div className="route-info">
                                    <h3>{route.label}</h3>
                                    <p>{route.description}</p>
                                </div>
                                <button
                                    onClick={() => invalidateCache(route.key)}
                                    disabled={loading}
                                    className="clear-cache-button"
                                >
                                    {loading ? (
                                        <><i className="fas fa-spinner fa-spin"></i> Tømmer...</>
                                    ) : (
                                        <><i className="fas fa-trash"></i> Tøm cache</>
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="clear-all-section">
                        <div className="section-header">
                            <h2>
                              Tøm all cache
                            </h2>
                        </div>
                        <div className="warning-box">
                            <i className="fas fa-exclamation-circle"></i>
                            <p>
                                Dette vil tømme <strong>ALLE</strong> cache-oppføringer på tvers av alle endepunkter. 
                                Denne handlingen kan ikke angres og kan midlertidig øke serverbelastningen.
                            </p>
                        </div>
                        <button
                            onClick={clearAllCache}
                            disabled={loading}
                            className="clear-all-button"
                        >
                            {loading ? (
                                <><i className="fas fa-spinner fa-spin"></i> Tømmer alt...</>
                            ) : (
                                <><i className="fas fa-exclamation-circle"></i> Tøm all cache</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

